import { useEffect, useState, type ComponentType } from 'react';
import { ArrowRight, ArrowUpRight, X } from 'react-bootstrap-icons';
import { FaAws, FaGithub, FaLinkedin, FaMicrosoft } from 'react-icons/fa';
import {
  SiFastapi,
  SiFigma,
  SiGit,
  SiHuggingface,
  SiLinux,
  SiMongodb,
  SiNvidia,
  SiPandas,
  SiPostgresql,
  SiPython,
  SiPytorch,
  SiRabbitmq,
  SiReact,
  SiTypescript,
} from 'react-icons/si';
import './App.css';

type BrandIconComponent = ComponentType<{
  'aria-hidden'?: boolean | 'true' | 'false';
  className?: string;
}>;

const GitHubIcon = FaGithub as unknown as BrandIconComponent;
const HuggingFaceIcon = SiHuggingface as unknown as BrandIconComponent;
const LinkedInIcon = FaLinkedin as unknown as BrandIconComponent;
const AwsIcon = FaAws as unknown as BrandIconComponent;
const MicrosoftIcon = FaMicrosoft as unknown as BrandIconComponent;
const FastApiIcon = SiFastapi as unknown as BrandIconComponent;
const FigmaIcon = SiFigma as unknown as BrandIconComponent;
const GitIcon = SiGit as unknown as BrandIconComponent;
const LinuxIcon = SiLinux as unknown as BrandIconComponent;
const MongoDbIcon = SiMongodb as unknown as BrandIconComponent;
const NvidiaIcon = SiNvidia as unknown as BrandIconComponent;
const PandasIcon = SiPandas as unknown as BrandIconComponent;
const PostgreSqlIcon = SiPostgresql as unknown as BrandIconComponent;
const PythonIcon = SiPython as unknown as BrandIconComponent;
const PytorchIcon = SiPytorch as unknown as BrandIconComponent;
const RabbitMqIcon = SiRabbitmq as unknown as BrandIconComponent;
const ReactIcon = SiReact as unknown as BrandIconComponent;
const TypeScriptIcon = SiTypescript as unknown as BrandIconComponent;

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const publicAssetUrl = (assetPath: string) => {
  const publicPath = (process.env.PUBLIC_URL ?? '').replace(/\/$/, '');
  return `${publicPath}/${assetPath.replace(/^\//, '')}`;
};

type StoryImageFit = 'cover' | 'contain';

interface PortfolioStory {
  id: string;
  category: string;
  title: string;
  intro: string;
  description: string;
  image: string;
  alt: string;
  gallery?: readonly {
    src: string;
    alt: string;
  }[];
  facts: readonly string[];
  fit?: StoryImageFit;
  link?: string;
  linkLabel?: string;
}

interface SkillDomain {
  id: string;
  title: string;
  summary: string;
  intro: string;
  description: string;
  tools: readonly string[];
  logos: readonly {
    name: string;
    icon: BrandIconComponent;
  }[];
}

interface TimelineItem {
  period: string;
  title: string;
  place: string;
  description: string;
}

const skillDomains: SkillDomain[] = [
  {
    id: 'ai-language',
    title: 'AI, LLM & sprog',
    summary: 'RAG · LLM · PyTorch',
    intro: 'Jeg bygger sprogmodeller ind i produkter, hvor svarene skal være relevante, evaluerbare og til at stole på.',
    description: 'Mit arbejde spænder fra RAG-arkitektur og retrieval til fine-tuning, promptdesign og systematisk evaluering. Jeg har særligt arbejdet med dansk NLP, medicinske opsummeringer, tokenizers og modeller trænet på LUMI.',
    tools: ['RAG-arkitektur', 'LLM fine-tuning', 'PyTorch', 'Hugging Face', 'Prompt & eval'],
    logos: [
      { name: 'PyTorch', icon: PytorchIcon },
      { name: 'Hugging Face', icon: HuggingFaceIcon },
      { name: 'NVIDIA GPU', icon: NvidiaIcon },
    ],
  },
  {
    id: 'backend-async',
    title: 'Backend & async',
    summary: 'FastAPI · Dramatiq · MQ',
    intro: 'AI-jobs tager tid. Derfor bygger jeg backends, hvor køer, workers og API’er holder produktet hurtigt og driften stabil.',
    description: 'Jeg bruger Python og FastAPI til services og Dramatiq til baggrundsjobs, retries og længere modelkørsler. Message queues gør det muligt at adskille brugerens request fra tung behandling og skalere de dele af systemet, der faktisk har brug for det.',
    tools: ['Python', 'FastAPI', 'Dramatiq', 'Message queues', 'REST APIs'],
    logos: [
      { name: 'Python', icon: PythonIcon },
      { name: 'FastAPI', icon: FastApiIcon },
      { name: 'Dramatiq / MQ', icon: RabbitMqIcon },
    ],
  },
  {
    id: 'frontend-product',
    title: 'Frontend & produkt',
    summary: 'TypeScript · React · Figma',
    intro: 'Jeg arbejder med interfaces som en del af produktet - ikke som pynt oven på teknikken.',
    description: 'Fra tidlige Figma-prototyper til React- og React Native-flows arbejder jeg med informationsarkitektur, conversational UI og de små interaktioner, der gør komplekse AI-funktioner rolige og forståelige.',
    tools: ['TypeScript', 'React', 'React Native', 'Conversational UI', 'Figma'],
    logos: [
      { name: 'TypeScript', icon: TypeScriptIcon },
      { name: 'React', icon: ReactIcon },
      { name: 'Figma', icon: FigmaIcon },
    ],
  },
  {
    id: 'cloud-infrastructure',
    title: 'Cloud & infrastruktur',
    summary: 'AWS · Azure · Lokal GPU',
    intro: 'Jeg kan lide at vide, hvor modellen faktisk kører, og hvad der sker efter et deploy.',
    description: 'Jeg har arbejdet med AWS og Azure samt lokale GPU-miljøer, hvor private data skal blive i huset. Fokus er på en enkel arkitektur, tydelige grænser og drift, der passer til produktets krav til sikkerhed, performance og økonomi.',
    tools: ['AWS', 'Azure', 'Lokal GPU-drift', 'Linux', 'Cloud-arkitektur'],
    logos: [
      { name: 'AWS', icon: AwsIcon },
      { name: 'Microsoft Azure', icon: MicrosoftIcon },
      { name: 'Linux', icon: LinuxIcon },
      { name: 'NVIDIA GPU', icon: NvidiaIcon },
    ],
  },
  {
    id: 'data-models',
    title: 'Data & modeller',
    summary: 'SQL · NoSQL · Pipelines',
    intro: 'En model er kun så god som dataarbejdet, evalueringen og de beslutninger, den skal understøtte.',
    description: 'Jeg har bygget dataflows, predictive modeller, zero-shot classifiers og analyser på tværs af strukturerede og ustrukturerede data. Jeg arbejder med både SQL og NoSQL og gør modellens kvalitet synlig gennem relevante tests og målinger.',
    tools: ['SQL', 'NoSQL', 'Data pipelines', 'Zero-shot', 'Model evaluation'],
    logos: [
      { name: 'PostgreSQL', icon: PostgreSqlIcon },
      { name: 'MongoDB', icon: MongoDbIcon },
      { name: 'Pandas', icon: PandasIcon },
    ],
  },
  {
    id: 'open-source-research',
    title: 'Open source & research',
    summary: 'Hugging Face · MorSeD · LUMI',
    intro: 'Jeg går op i, at modeller, benchmarks og erfaringer kan efterprøves og bruges af andre.',
    description: 'Jeg har udgivet danske modelvarianter på Hugging Face og været med til at udvikle MorSeD, et ekspertannoteret benchmark for danske morfemer. Open source gør forskningen mere brugbar og skaber et bedre udgangspunkt for den næste, der bygger videre.',
    tools: ['Open source', 'MorSeD', 'LUMI', 'Benchmarks', 'Reproducibility'],
    logos: [
      { name: 'GitHub', icon: GitHubIcon },
      { name: 'Git', icon: GitIcon },
      { name: 'Hugging Face', icon: HuggingFaceIcon },
    ],
  },
];

const timelineItems: TimelineItem[] = [
  {
    period: '2017–2019',
    title: 'Mennesker først',
    place: 'Margretheskolen',
    description: 'Klasseledelse og støtte til elever med særlige behov.',
  },
  {
    period: '2019–2022',
    title: 'Digital formidling',
    place: 'Gyldendal',
    description: 'Læringsplatforme, content flows og digitale komponenter.',
  },
  {
    period: '2019–2022',
    title: 'Design & teknologi',
    place: 'ITU · BSc',
    description: 'Co-design, data, fysisk computing og brugerinterfaces.',
  },
  {
    period: '2022',
    title: 'UX i praksis',
    place: 'WDP X',
    description: 'Workshops, interviews og prototyper til kundeprojekter.',
  },
  {
    period: '2022–2025',
    title: 'Software Design',
    place: 'ITU · MSc',
    description: 'Applied AI, systemer, algoritmer og software engineering.',
  },
  {
    period: '2023–2025',
    title: 'AI i organisationen',
    place: 'SGAV',
    description: 'My, ML/NLP-workflows og lokale GPU-løsninger.',
  },
  {
    period: '2025–nu',
    title: 'Data & fællesskab',
    place: 'Roskilde Festival · Team lead',
    description: 'Cirka 15 frivillige, publikumsstudier og brugbare dataindsigter.',
  },
  {
    period: '2025–nu',
    title: 'Fra forskning til virksomhed',
    place: 'Rye AI · Co-founder',
    description: 'meoo, AI-produkter og egen infrastruktur.',
  },
];

const portfolioStories: PortfolioStory[] = [
  {
    id: 'rye-ai',
    category: 'Co-founder · AI-produkter',
    title: 'Rye AI - fra model til maskinrum',
    intro: 'I Rye AI bygger vi AI-produkter hele vejen fra idé og interface til den infrastruktur, modellerne kører på.',
    description: 'Jeg arbejder på tværs af produktretning, brugeroplevelse, LLM-pipelines og drift. Det betyder, at jeg både kan diskutere den menneskelige oplevelse og stå med hænderne i et serverrack, når det er dét, der skal til. Vi eksperimenterer hurtigt, men bygger med blik for privatliv, stabilitet og den daglige brug efter den første demo.',
    image: '/RyeAI/Server/cover.jpeg',
    alt: 'Rye AI-teamet foran deres serverrack',
    gallery: [
      {
        src: '/RyeAI/Server/IMG_1737%20Large.jpeg',
        alt: 'Mikkel arbejder med en ny AI-server',
      },
      {
        src: '/RyeAI/Server/IMG_1803%20Large.jpeg',
        alt: 'Rye AI-teamet monterer hardware i serveren',
      },
      {
        src: '/RyeAI/Server/IMG_1807%20Large.jpeg',
        alt: 'AI-server monteret i et serverrack',
      },
      {
        src: '/RyeAI/Server/IMG_0101%20Large.jpeg',
        alt: 'AI-server klargjort på arbejdsbordet',
      },
      {
        src: '/RyeAI/Server/IMG_1738%20Large.jpeg',
        alt: 'Lokal AI-server under opsætning',
      },
      {
        src: '/RyeAI/Server/IMG_0257%20Large.jpeg',
        alt: 'GPU-kort og serverkomponenter klar til montering',
      },
      {
        src: '/RyeAI/Server/IMG_0315%20Large.jpeg',
        alt: 'Rye AI-teamet ved den færdige serverinstallation',
      },
      {
        src: '/RyeAI/Server/IMG_0251%20Large.jpeg',
        alt: 'Hardware til Rye AI-serveren under samling',
      },
      {
        src: '/RyeAI/Server/IMG_0261%20Large.jpeg',
        alt: 'GPU-kort monteret i AI-serveren',
      },
    ],
    facts: ['Produktstrategi', 'LLM-pipelines', 'AI-infrastruktur', 'Conversational UI'],
  },
  {
    id: 'meoo',
    category: 'AI · Produkt',
    title: 'meoo - en partner i din verden',
    intro: 'meoo er et AI-drevet produkt, der gør kompleks information mere overskuelig og skaber plads til refleksion, inspiration og handling.',
    description: 'Som co-founder former jeg produktets flows og interfaces og arbejder med AI, der samler, strukturerer og formidler information på en enkel måde. meoo udforsker, hvordan teknologi kan blive en relevant støtte i forskellige dele af hverdagen.',
    image: '/RyeAI/Meoo/cover.webp',
    alt: 'meoo-logo på grøn og creme baggrund',
    gallery: [
      {
        src: '/RyeAI/Meoo/logo.png',
        alt: 'meoo-logo med navn',
      },
    ],
    facts: ['React Native', 'AI-assisteret overblik', 'Produktdesign', 'AI-produkt'],
    fit: 'contain',
    link: 'https://meoo.ai/',
    linkLabel: 'meoo.ai',
  },
  // {
  //   id: 'my-sgav',
  //   category: 'SGAV · AI i praksis',
  //   title: 'My - lokal AI til 1.000+ brugere',
  //   intro: 'Jeg byggede den interne chatbot My fra bunden og var med til at gøre den til et dagligt værktøj for mere end 1.000 brugere i SGAV.',
  //   description: 'Løsningen blev bygget til at køre helt lokalt på GPU-infrastruktur, så data kunne blive i huset. Jeg udviklede RAG-arkitektur, retrieval, prompts og brugerflow og fulgte løsningen gennem evaluering, deployment og løbende forbedringer. Projektet lærte mig, hvad der skal til, når en AI-prototype skal være stabil, forståelig og nyttig for en hel organisation.',
  //   image: '/RyeAI/Server/IMG_0261%20Large.jpeg',
  //   alt: 'GPU-kort monteret i en lokal AI-server',
  //   gallery: [
  //     {
  //       src: '/RyeAI/Server/IMG_0257%20Large.jpeg',
  //       alt: 'GPU-kort og serverkomponenter klar til montering',
  //     },
  //     {
  //       src: '/RyeAI/Server/IMG_1738%20Large.jpeg',
  //       alt: 'Lokal AI-server under opsætning',
  //     },
  //   ],
  //   facts: ['1.000+ brugere', 'Lokale GPU’er', 'RAG & NLP', 'Bygget fra bunden'],
  // },
  {
    id: 'open-source',
    category: 'Open source · Forskning',
    title: 'Open source & dansk sprogteknologi',
    intro: 'Jeg går meget op i, at forskning og værktøjer kan bruges af andre - ikke kun ende i en rapport eller på min egen computer.',
    description: 'Jeg har fine-tunet DanSumT5 og mT5 på danske sundhedstekster på LUMI og udgivet fire modelvarianter på Hugging Face. Jeg har også arbejdet med en dansk subword-tokenizer og været med til at udvikle MorSeD, det første ekspertannoterede benchmark for danske morfemer. MorSeD blev accepteret på NoDaLiDa 2025, så andre kan bygge videre på arbejdet.',
    image: '/RyeAI/Open%20source/BE6D5498-AD98-4D84-9882-A4198DA4D550_1_105_c.jpeg',
    alt: 'Træning af danske sprogmodeller på LUMI-supercomputeren',
    facts: ['MorSeD', 'NoDaLiDa 2025', 'Hugging Face', 'LUMI'],
    link: 'https://huggingface.co/MikkelWK',
    linkLabel: 'Se modellerne',
  },
  {
    id: 'chinatown',
    category: 'Makerprojekt · Lyd',
    title: 'Chinatown - to tons lyd',
    intro: 'En mobil, soldrevet højttaler bygget fra rå plader til færdigt anlæg til Roskilde Festival.',
    description: 'Projektet samlede alt det, jeg holder af: økonomi, sponsorater, CNC-fræsning, elektronik og fysisk design. Med et budget omkring 150.000 kr. krævede det både planlægning og koordinering. Jeg lavede vektorfiler og G-code, byggede kabinetter og integrerede Arduino og Raspberry Pi til musikstyret lys, strøm og styring.',
    image: '/H%C3%B8jtalereChinatown/53436902-7472-4BDF-8F75-AB29C2352BC6_1_105_c.jpeg',
    alt: 'Den færdige Chinatown-højttaler',
    gallery: [
      {
        src: '/H%C3%B8jtalereChinatown/07701B4B-8A99-466A-9742-03D5EB010483_1_105_c.jpeg',
        alt: 'Chinatown-højttaleren på Roskilde Festival ved solnedgang',
      },
      {
        src: '/H%C3%B8jtalereChinatown/E100E3E5-0EA8-4834-9B75-9A1499B24E4B_1_105_c.jpeg',
        alt: 'CNC-fræsning af dele til Chinatown-højttaleren',
      },
      {
        src: '/H%C3%B8jtalereChinatown/04C97C3B-4FEE-4890-A639-90AD148C697C_1_105_c.jpeg',
        alt: 'Højttalerkabinet under opbygning',
      },
      {
        src: '/H%C3%B8jtalereChinatown/61CF2599-064F-4874-BE89-42CD625FB56D_1_105_c.jpeg',
        alt: 'Procesbillede fra byggeriet af Chinatown-højttaleren',
      },
      {
        src: '/H%C3%B8jtalereChinatown/BCF0E9FA-2B72-4857-81F2-A46351A3376C_1_105_c.jpeg',
        alt: 'Detalje fra opbygningen af Chinatown-anlægget',
      },
      {
        src: '/H%C3%B8jtalereChinatown/0D420BB3-54FC-4070-B803-A32BD4914310_1_105_c.jpeg',
        alt: 'Chinatown-højttaleren under konstruktion',
      },
      {
        src: '/H%C3%B8jtalereChinatown/C4F0267D-C71D-4306-9BE7-E54F694AFA98_1_105_c.jpeg',
        alt: 'Arbejdet med Chinatown-højttalerens kabinetter',
      },
      {
        src: '/H%C3%B8jtalereChinatown/6383185E-56EC-4659-A6DA-8A960C05EB6A_1_105_c.jpeg',
        alt: 'Elektronik og konstruktion i Chinatown-projektet',
      },
      {
        src: '/H%C3%B8jtalereChinatown/E960A2D8-2B55-4B2E-B873-0E8F553C6C06_1_105_c.jpeg',
        alt: 'Maling og udsmykning af Chinatown-højttaleren',
      },
      {
        src: '/H%C3%B8jtalereChinatown/01591640-3E4F-461E-B17B-71F71FEE39E4_1_105_c.jpeg',
        alt: 'Chinatown-anlægget under den sidste del af byggeriet',
      },
      {
        src: '/H%C3%B8jtalereChinatown/46E7E24E-A645-46C0-855E-3E156DAF00FD_1_105_c.jpeg',
        alt: 'Detaljer på det færdige Chinatown-lydsystem',
      },
      {
        src: '/H%C3%B8jtalereChinatown/8091536D-DFA9-4285-B230-C611DF450C9B_1_105_c.jpeg',
        alt: 'Chinatown-højttaleren klar til festival',
      },
    ],
    facts: ['2 tons', 'CNC & G-code', 'Arduino', 'Solenergi'],
  },
  {
    id: 'dj',
    category: 'Musik · Events',
    title: 'DJ - teknik, timing og mennesker',
    intro: 'Når jeg står bag en DJ-pult, handler teknik ikke om specifikationer, men om at læse et rum og skabe den rigtige energi.',
    description: 'DJ-arbejdet er en anden side af den samme nysgerrighed: lydsystemer, signalveje og udstyr skal fungere, men den egentlige opgave er menneskelig. Jeg forbereder retning og teknik, men lader aftenen forme valgene. Man skal lytte, reagere og bygge en oplevelse sammen med både publikum og de mennesker, man står bag pulten med.',
    image: '/DJ/F2622F8E-F612-44A0-ACF5-2CABAB0DCE53_1_105_c.jpeg',
    alt: 'Mikkel og holdet bag en DJ-pult',
    facts: ['DJ', 'Lydteknik', 'Events', 'Samarbejde'],
  },
  {
    id: 'roskilde',
    category: 'Frivillighed · Data',
    title: 'Roskilde Festival - data midt i virkeligheden',
    intro: 'Jeg er team lead for cirka 15 frivillige, der undersøger publikumsoplevelser og gør festivaladfærd til brugbare indsigter.',
    description: 'Vi arbejder med publikumsstudier, ad hoc-analyser og frivilligundersøgelser på tværs af festivalen. Jeg hjælper med at planlægge opgaverne, støtte teamet og omsætte observationer til klare resultater. Det er dataarbejde med mudder på skoene: input skal samles hurtigt, analyseres ansvarligt og formidles, mens det stadig kan bruges.',
    image: '/RoskildeFestival/9F5009F3-93AC-447F-8B7F-CE8BFDD444D9_4_5005_c.jpeg',
    alt: 'Publikum foran Orange Scene på Roskilde Festival',
    facts: ['Team lead', '~15 frivillige', 'Publikumsstudier', 'Dataanalyse'],
  },
  {
    id: 'research-to-startup',
    category: 'Formidling · Startup',
    title: 'Fra forskningsprojekt til startup',
    intro: 'Jeg deler gerne processen - også de usikre mellemregninger mellem en god idé og noget, der fungerer som virksomhed.',
    description: 'Rejsen fra studie- og forskningsprojekter til Rye AI har lært mig at præsentere komplekse valg klart, invitere andre ind i processen og gøre tekniske muligheder konkrete for mennesker med forskellige fagligheder. Talks og pitches er samtidig en måde at teste fortællingen på: Hvis idéen ikke kan forklares enkelt, er produktet ofte heller ikke skarpt nok endnu.',
    image: '/Speaker/636C11FB-CBF1-4A67-ABBC-7E7D767C8EF2_1_105_c.jpeg',
    alt: 'Mikkel præsenterer rejsen fra forskningsprojekt til startup',
    facts: ['Formidling', 'Pitch', 'Produktvision', 'Tværfagligt arbejde'],
  },
  // {
  //   id: 'communities',
  //   category: 'Fællesskaber · Iværksætteri',
  //   title: 'Miljøer, der skubber idéer videre',
  //   intro: 'Gode produkter bliver sjældent til alene. Jeg har været en del af miljøer, hvor sparring, sundhedsfaglighed og iværksætteri mødes.',
  //   description: 'Gennem ITU NextGen og Health Innovators har jeg fået perspektiver på både forretning, produkt og sundhed. Feedback fra andre founders, fagpersoner og brugere har udfordret antagelser og gjort retningen skarpere. De fællesskaber har været vigtige i overgangen fra forskningsprojekt til virksomhed - og minder mig om, at gode idéer vokser bedst i dialog.',
  //   image: '/APartOF/ITUNextGen.jpeg',
  //   alt: 'ITU NextGen-logo',
  //   gallery: [
  //     {
  //       src: '/APartOF/HealthInnovators.png',
  //       alt: 'Health Innovators-logo',
  //     },
  //   ],
  //   facts: ['ITU NextGen', 'Health Innovators', 'Sparring', 'Iværksætteri'],
  //   fit: 'contain',
  // },
];

function App() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeStoryId, setActiveStoryId] = useState(portfolioStories[0].id);
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeSkillId, setActiveSkillId] = useState(skillDomains[0].id);
  const [skillDetailOpen, setSkillDetailOpen] = useState(false);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [previousImageIndex, setPreviousImageIndex] = useState<number | null>(null);

  const activeStoryIndex = Math.max(
    portfolioStories.findIndex((story) => story.id === activeStoryId),
    0
  );
  const activeStory = portfolioStories[activeStoryIndex];
  const activeStoryImages = [
    { src: activeStory.image, alt: activeStory.alt },
    ...(activeStory.gallery ?? []),
  ];
  const activeSkillIndex = Math.max(
    skillDomains.findIndex((domain) => domain.id === activeSkillId),
    0
  );
  const activeSkill = skillDomains[activeSkillIndex];

  useEffect(() => {
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setDetailOpen(false);
        setSkillDetailOpen(false);
      }
    };

    document.body.classList.toggle('menu-open', menuOpen || detailOpen || skillDetailOpen);
    window.addEventListener('keydown', closeOnEscape);

    return () => {
      document.body.classList.remove('menu-open');
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [menuOpen, detailOpen, skillDetailOpen]);

  useEffect(() => {
    const revealItems = Array.from(document.querySelectorAll<HTMLElement>('[data-reveal]'));
    const timeline = document.querySelector<HTMLElement>('[data-timeline]');

    if (!('IntersectionObserver' in window) || prefersReducedMotion()) {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      timeline?.classList.add('is-visible');
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.16 }
    );

    const fallbackId = window.setTimeout(() => {
      revealItems.forEach((item) => item.classList.add('is-visible'));
      timeline?.classList.add('is-visible');
    }, 15000);

    revealItems.forEach((item) => observer.observe(item));
    if (timeline) {
      observer.observe(timeline);
    }
    return () => {
      window.clearTimeout(fallbackId);
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    setActiveImageIndex(0);
    setPreviousImageIndex(null);

    if (
      !detailOpen ||
      activeStoryImages.length < 2 ||
      prefersReducedMotion()
    ) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setActiveImageIndex((currentIndex) => {
        setPreviousImageIndex(currentIndex);
        return (currentIndex + 1) % activeStoryImages.length;
      });
    }, 4000);

    return () => window.clearInterval(intervalId);
  }, [activeStoryId, activeStoryImages.length, detailOpen]);

  const closeMenu = () => setMenuOpen(false);

  const openStory = (storyId: string) => {
    setMenuOpen(false);
    setSkillDetailOpen(false);
    setActiveStoryId(storyId);
    setActiveImageIndex(0);
    setPreviousImageIndex(null);
    setDetailOpen(true);
  };

  const openSkill = (skillId: string) => {
    setMenuOpen(false);
    setDetailOpen(false);
    setActiveSkillId(skillId);
    setSkillDetailOpen(true);
  };

  return (
    <div className="site-shell" id="top">
      <a className="skip-link" href="#indhold">Gå til indhold</a>

      <header className="site-header">
        <button
          className={`menu-button${menuOpen ? ' is-close' : ''}`}
          type="button"
          aria-label={menuOpen ? 'Luk menu' : 'Åbn menu'}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((isOpen) => !isOpen)}
        >
          <span className="menu-icon" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>
      </header>

      <nav
        className={`menu-overlay ${menuOpen ? 'is-open' : ''}`}
        aria-label="Primær navigation"
        aria-hidden={!menuOpen}
      >
        <div className="menu-body">
          <div className="menu-links">
            <a href="#kompetencer" onClick={closeMenu}>Kompetencer</a>
            <a href="#arbejde" onClick={closeMenu}>Mine spor</a>
            <a href="#om" onClick={closeMenu}>Om</a>
            <a href="#kontakt" onClick={closeMenu}>Kontakt</a>
          </div>
          <figure className="menu-portrait" aria-hidden="true">
            <div className="menu-portrait-frame">
              <img src={publicAssetUrl('/ME/mikkel_large.jpeg')} alt="" decoding="async" />
            </div>
          </figure>
        </div>
        <div className="menu-meta">
          <div className="menu-roles">
            <span>AI engineer</span>
            <span>Co-founder</span>
            <span>Maker</span>
          </div>
          <div className="menu-socials">
            <a href="https://github.com/MrOkay17" target="_blank" rel="noreferrer">
              <GitHubIcon aria-hidden="true" /><span>GitHub</span>
            </a>
            <a href="https://huggingface.co/MikkelWK" target="_blank" rel="noreferrer">
              <HuggingFaceIcon aria-hidden="true" /><span>Hugging Face</span>
            </a>
            <a href="https://www.linkedin.com/in/mikkel-kildeberg-17769b185/" target="_blank" rel="noreferrer">
              <LinkedInIcon aria-hidden="true" /><span>LinkedIn</span>
            </a>
          </div>
        </div>
      </nav>

      <aside
        className={`detail-overlay tone-${(activeStoryIndex % 4) + 1}${detailOpen ? ' is-open' : ''}`}
        id="portfolio-detail"
        role="dialog"
        aria-modal={detailOpen}
        aria-hidden={!detailOpen}
        aria-labelledby="detail-title"
      >
        <div className="detail-topbar">
          <p>{String(activeStoryIndex + 1).padStart(2, '0')} / {String(portfolioStories.length).padStart(2, '0')} · {activeStory.category}</p>
          <button className="detail-close" type="button" aria-label="Luk projektdetaljer" onClick={() => setDetailOpen(false)}>
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="detail-shell">
          <div className="detail-heading">
            <h2 id="detail-title">{activeStory.title}</h2>
            <p>{activeStory.intro}</p>
          </div>
          <div className="detail-content">
            <figure className={`detail-media${activeStory.fit === 'contain' ? ' contain' : ''}`}>
              {activeStoryImages.map((image, imageIndex) => (
                <img
                  className={
                    imageIndex === activeImageIndex
                      ? 'is-active'
                      : imageIndex === previousImageIndex
                        ? 'is-previous'
                        : ''
                  }
                  src={publicAssetUrl(image.src)}
                  alt={image.alt}
                  loading={detailOpen && imageIndex < 2 ? 'eager' : 'lazy'}
                  decoding="async"
                  key={image.src}
                />
              ))}
            </figure>
            <div className="detail-copy">
              <p>{activeStory.description}</p>
              <ul aria-label={`Nøgleord for ${activeStory.title}`}>
                {activeStory.facts.map((fact) => <li key={fact}>{fact}</li>)}
              </ul>
              {activeStory.link && activeStory.linkLabel && (
                <a className="text-link" href={activeStory.link} target="_blank" rel="noreferrer">
                  {activeStory.linkLabel} <ArrowUpRight aria-hidden="true" />
                </a>
              )}
            </div>
          </div>
        </div>
      </aside>

      <aside
        className={`detail-overlay skill-detail-overlay tone-${(activeSkillIndex % 4) + 1}${skillDetailOpen ? ' is-open' : ''}`}
        id="skill-detail"
        role="dialog"
        aria-modal={skillDetailOpen}
        aria-hidden={!skillDetailOpen}
        aria-labelledby="skill-detail-title"
      >
        <div className="detail-topbar">
          <p>{String(activeSkillIndex + 1).padStart(2, '0')} / {String(skillDomains.length).padStart(2, '0')} · Kompetencer</p>
          <button className="detail-close" type="button" aria-label="Luk kompetencedetaljer" onClick={() => setSkillDetailOpen(false)}>
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="detail-shell skill-detail-shell">
          <div className="detail-heading">
            <h2 id="skill-detail-title">{activeSkill.title}</h2>
            <p>{activeSkill.intro}</p>
          </div>
          <div className="skill-detail-content">
            <div className="skill-detail-copy">
              <p>{activeSkill.description}</p>
              <ul aria-label={`Værktøjer inden for ${activeSkill.title}`}>
                {activeSkill.tools.map((tool) => <li key={tool}>{tool}</li>)}
              </ul>
            </div>
            <div className="skill-logo-grid" aria-label={`Logoer inden for ${activeSkill.title}`}>
              {activeSkill.logos.map((logo) => {
                const Icon = logo.icon;
                return (
                  <div className="skill-logo" key={logo.name}>
                    <Icon aria-hidden="true" />
                    <span>{logo.name}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </aside>

      <main id="indhold">
        <section className="hero" aria-labelledby="hero-title">
          <div className="hero-copy">
            <h1 id="hero-title">Kode. Idéer.<br />Og alt<br />derimellem.</h1>
            <p>Jeg er AI engineer, co-founder og full-stack udvikler med en forkærlighed for roligt design. Jeg bygger alt fra LLM-pipelines og interfaces til højttalere i fuld skala.</p>
          </div>
          <picture className="hero-picture">
            <source srcSet={publicAssetUrl('/hero-server.avif')} type="image/avif" />
            <img
              className="hero-media"
              src={publicAssetUrl('/hero-server.jpg')}
              alt="Mikkel Wildner Kildeberg arbejder med en server"
              {...{ fetchpriority: 'high' }}
              decoding="async"
            />
          </picture>
        </section>

        <section className="cases" id="arbejde" aria-labelledby="cases-title">
          <h2 className="display-title" id="cases-title">Mine spor</h2>
          <div className="case-grid">
            {portfolioStories.map((story, index) => {
              return (
                <button
                  className={`case-card reveal-${(index % 4) + 1}`}
                  type="button"
                  data-reveal
                  aria-expanded={detailOpen && activeStoryId === story.id}
                  aria-controls="portfolio-detail"
                  onClick={() => openStory(story.id)}
                  key={story.id}
                >
                  <img
                    className={`portfolio-image ${index % 3 === 0 ? 'landscape' : 'wide'}${story.fit === 'contain' ? ' contain' : ''}`}
                    src={publicAssetUrl(story.image)}
                    alt={story.alt}
                    loading="lazy"
                    decoding="async"
                  />
                  <p>{story.category}</p>
                  <h3>{story.title}</h3>
                </button>
              );
            })}
          </div>
        </section>

        <section className="disciplines" id="kompetencer" aria-labelledby="disciplines-title">
          <p className="lead" id="disciplines-title">
            Jeg følger gerne en idé hele vejen: fra research og model til
            interface, infrastruktur og et produkt, mennesker kan bruge.
          </p>
          <div className="discipline-grid">
            {skillDomains.map((domain) => (
              <button
                className="discipline"
                type="button"
                aria-expanded={skillDetailOpen && activeSkillId === domain.id}
                aria-controls="skill-detail"
                onClick={() => openSkill(domain.id)}
                key={domain.id}
              >
                <span>{domain.title}</span>
                <small>{domain.summary}</small>
              </button>
            ))}
          </div>
          <a className="pill-link" href="#arbejde">
            Se det, jeg arbejder med <ArrowRight aria-hidden="true" />
          </a>
        </section>

        <section className="timeline-section" id="erfaring" aria-labelledby="timeline-title" data-timeline>
          <div className="timeline-inner">
            <div className="timeline-heading">
              <div className="timeline-heading-copy">
                <h2 id="timeline-title">Fra mennesker til modeller.</h2>
                <p className="timeline-intro">Min vej ind i AI begyndte ikke med en model. Den begyndte med mennesker, formidling og lysten til at forstå, hvordan ting virker.</p>
              </div>
              <figure className="timeline-personal">
                <img src={publicAssetUrl('/RyeAI/Server/Mikkel%20GPU.jpeg')} alt="Mikkel med GPU-kort til en lokal AI-server" loading="lazy" decoding="async" />
              </figure>
            </div>
            <div className="timeline-window">
              <div className="timeline-track">
                <div className="timeline-axis" aria-hidden="true"><span /></div>
                {timelineItems.map((item, index) => (
                  <article
                    className={`timeline-event timeline-tone-${(index % 4) + 1}${index === timelineItems.length - 1 ? ' is-current' : ''}`}
                    aria-label={`${item.period}: ${item.title}, ${item.place}`}
                    tabIndex={0}
                    key={`${item.period}-${item.title}`}
                  >
                    <div className="timeline-dot" aria-hidden="true" />
                    <div className="timeline-content">
                      <p>{item.period}</p>
                      <h3>{item.title}</h3>
                      <h4>{item.place}</h4>
                      <p>{item.description}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="about" id="om" aria-labelledby="about-title">
          <div className="about-mark">
            <img src={publicAssetUrl('/ME/mikkel_large.jpeg')} alt="Portræt af Mikkel Wildner Kildeberg" loading="lazy" decoding="async" />
          </div>
          <div className="about-copy">
            <h2 id="about-title">Jeg er både AI engineer, produktbygger, leder og maker.</h2>
            <p>Min vej ind i tech begyndte med højttalere, Arduino og Raspberry Pi i garagen. I dag er jeg co-founder i Rye AI og har en MSc i Software Design - men nysgerrigheden og lysten til selv at bygge er stadig den samme.</p>
            <a className="text-link" href="https://www.linkedin.com/in/mikkel-kildeberg-17769b185/" target="_blank" rel="noreferrer">
              Find mig på LinkedIn <ArrowRight aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <footer className="site-footer" id="kontakt">
        <div className="footer-top">
          <p>Skal vi bygge noget sammen?</p>
          <a href="https://www.linkedin.com/in/mikkel-kildeberg-17769b185/" target="_blank" rel="noreferrer">
            Lad os tale sammen <ArrowUpRight aria-hidden="true" />
          </a>
        </div>
        <div className="footer-bottom">
          <p>©Mikkel Wildner Kildeberg {new Date().getFullYear()}</p>
          <div>
            <a href="https://github.com/MrOkay17" target="_blank" rel="noreferrer">
              <GitHubIcon aria-hidden="true" /><span>GitHub</span>
            </a>
            <a href="https://huggingface.co/MikkelWK" target="_blank" rel="noreferrer">
              <HuggingFaceIcon aria-hidden="true" /><span>Hugging Face</span>
            </a>
            <a href="https://www.linkedin.com/in/mikkel-kildeberg-17769b185/" target="_blank" rel="noreferrer">
              <LinkedInIcon aria-hidden="true" /><span>LinkedIn</span>
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;